import React, {
  FC,
  useLayoutEffect,
  useState,
  SyntheticEvent,
  useCallback,
} from 'react';
import styled from 'styled-components/macro';
import { animated, config, useTransition } from 'react-spring';
import { pathOr } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { useFormValidation } from '@hox/frontend-utils/hooks/useFormValidation';
import { debounce } from '@hox/frontend-utils/ramda';
import { Heading3 } from '@hox/ui/Typography';
import { AnchorLinkButton } from '@hox/ui/Button';
import { CardLoadingOverlay } from '@hox/ui/CardLoadingOverlay';
import { MagicLinkSent } from 'components/MagicLinkSent';
import { SendMagicLoginLinkButton } from 'components/SendMagicLinkButton';
import { AuthInput } from 'components/AuthInput';
import {
  emailRegexp,
  getSearchParams,
  getVisibleButton,
  getSsoProviderName,
  getSsoUrl,
} from './lib';
import { useLoginInfo } from './hooks/useLoginInfo';
import { useMagicLinkData } from './hooks/useMagicLinkData';
import { ILoginProps } from './models';

const LoginStyled = styled.div`
  text-align: center;
  > ${Heading3} {
    margin-bottom: 2rem;
  }
`;

const LoginButtons = styled.div`
  width: 100%;
  margin-top: 2rem;
  text-align: center;
`;

const validationRules = {
  email: [
    {
      predicate: (val: string) => emailRegexp.test(val),
      message: (
        <FormattedMessage
          defaultMessage="Invalid email address"
          id="auth.login.validationError.invalidEmail"
          description="Validation error on email input field for invalid email address"
        />
      ),
    },
  ],
};

const Login: FC<ILoginProps> = ({ location }) => {
  const params = getSearchParams(location);
  const [visibleButton, setVisibleButton] = useState<
    undefined | 'sso' | 'magicLink'
  >(undefined);
  const { errors, values, handleChange, isValid } = useFormValidation(
    validationRules,
    {
      email: '',
    }
  );
  const loginInfo = useLoginInfo(isValid ? values.email : undefined);
  const [magicLinkData, requestMagicLink] = useMagicLinkData(
    values.email,
    params
  );
  const requestMagicLinkHandler = useCallback(() => requestMagicLink(), [
    requestMagicLink,
  ]);
  const formReady = errors.email.length === 0 && isValid && !!loginInfo;
  const isMagicLinkSent =
    magicLinkData && !magicLinkData.loading && !!magicLinkData.data;
  const loading = magicLinkData.loading;
  const ssoLinkHref = getSsoUrl(
    pathOr('', ['data', 'sso', 'endPoint'], loginInfo),
    params
  );

  //
  // Callbacks
  const handleChangeDebounced = useCallback(
    debounce(handleChange, 500, {
      trailing: true,
    }),
    [handleChange]
  );

  //
  // Effects

  useLayoutEffect(() => {
    const nextVisibleButton = formReady
      ? getVisibleButton(loginInfo)
      : undefined;

    if (visibleButton !== nextVisibleButton) {
      setVisibleButton(nextVisibleButton);
    }
  }, [formReady, loginInfo, setVisibleButton, visibleButton]);

  //
  // Transitions

  const buttonTransitions = useTransition(visibleButton, null, {
    config: config.stiff,
    from: { opacity: 0 },
    enter: () => async (next: (props: {}) => Promise<void>) => {
      // Delay enter animation 540ms
      await new Promise(resolve => setTimeout(resolve, 540));
      await next({
        opacity: 1,
      });
    },
    leave: { opacity: 0 },
    // React-spring types are wrong
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);

  return (
    <LoginStyled>
      {loading && <CardLoadingOverlay />}
      {isMagicLinkSent ? (
        <MagicLinkSent />
      ) : (
        <>
          <Heading3>
            <FormattedMessage
              id="auth.login.title"
              defaultMessage="Login"
              description="Title of login form"
            />
          </Heading3>

          <AuthInput
            outlined
            errored={!!(values.email.length && errors.email.length)}
            errorText={errors.email[0]}
            loading={loginInfo.loading}
            type="email"
            name="email"
            placeholder="Email"
            onChange={(e: SyntheticEvent) => {
              e.persist();
              handleChangeDebounced(e);
            }}
          />

          <LoginButtons>
            {buttonTransitions.map(
              ({ item: currentButton, props: spring, key }) => (
                <animated.div style={spring} key={key}>
                  {currentButton === 'sso' && (
                    <AnchorLinkButton color="primary" href={ssoLinkHref}>
                      <FormattedMessage
                        id="auth.login.sso.signin"
                        defaultMessage="{ssoProviderName} Sign In"
                        description="Text inside a button to use Single-Sign On method to authenticate, included with which SSO provider end-user is using"
                        values={{
                          ssoProviderName: getSsoProviderName(loginInfo),
                        }}
                      />
                    </AnchorLinkButton>
                  )}
                  {currentButton === 'magicLink' && (
                    <SendMagicLoginLinkButton
                      color="primary"
                      loading={loading}
                      onClick={requestMagicLinkHandler}
                    >
                      <FormattedMessage
                        id="auth.login.magicLink.request"
                        defaultMessage="Request a Magic Link"
                        description="Text inside a button to send a Magic Link authentication request"
                      />
                    </SendMagicLoginLinkButton>
                  )}
                </animated.div>
              )
            )}
          </LoginButtons>
        </>
      )}
    </LoginStyled>
  );
};

export { Login };
